/**
 * One-time pad do modo "Selo".
 *
 * O QR animado transfere 256 KB de `randombytes_buf`. Enquanto sobrar
 * material, as mensagens usam pad puro — sigilo incondicional, sem depender
 * de nenhuma hipotese computacional.
 *
 * DUAS DECISOES DE SEGURANCA QUE SE AFASTAM DO PEDIDO ORIGINAL, DE PROPOSITO:
 *
 * 1. **O pad e dividido em dois.** A metade de baixo pertence a quem iniciou
 *    o Selo, a de cima a quem respondeu. Com um pad unico compartilhado, os
 *    dois lados escrevendo ao mesmo tempo reutilizariam o mesmo trecho de
 *    keystream — e XOR de duas mensagens com a mesma chave quebra as duas.
 *
 * 2. **Cada mensagem consome `len + 32` bytes, nao `len`.** Os 32 extras sao
 *    a chave de um MAC BLAKE2b de uso unico. XOR puro nao autentica nada:
 *    sem MAC, qualquer bit do texto claro pode ser invertido em transito sem
 *    que o destinatario perceba.
 */
import { compareBytes, concat, equalConstantTime, readUint32BE, writeUint32BE } from './bytes.js'
import { DecryptionFailedError, PadExhaustedError } from './errors.js'
import { DOMAIN, kdf } from './kdf.js'
import { SIZES, sodium } from './sodium.js'

export const SEAL_PAD_BYTES = 256 * 1024
export const OTP_MAC_BYTES = 32
export const OTP_OFFSET_BYTES = 4
/** Abaixo disto a UI pede para renovar o Selo. */
export const OTP_LOW_WATERMARK = 0.05

export type PadRole = 'initiator' | 'responder'

/** Gera o material do Selo. So chame no aparelho, nunca no servidor. */
export function generateSealMaterial(sizeBytes: number = SEAL_PAD_BYTES): Uint8Array {
  return sodium().randombytes_buf(sizeBytes)
}

/**
 * Decide quem fica com qual metade sem precisar de negociacao: quem tem o
 * fingerprint menor em ordem lexicografica e o `initiator`.
 */
export function padRoleFor(ownFingerprint: Uint8Array, peerFingerprint: Uint8Array): PadRole {
  return compareBytes(ownFingerprint, peerFingerprint) < 0 ? 'initiator' : 'responder'
}

export class OtpPad {
  private sendOffset = 0
  /** Maior offset ja consumido na regiao de recepcao — evita reprocessar. */
  private readonly consumedOnReceive = new Set<number>()

  private constructor(
    private readonly material: Uint8Array,
    readonly role: PadRole,
  ) {}

  static fromSeal(material: Uint8Array, role: PadRole): OtpPad {
    if (material.length < 2 * (OTP_MAC_BYTES + 1)) {
      throw new PadExhaustedError(2 * (OTP_MAC_BYTES + 1), material.length)
    }
    return new OtpPad(material, role)
  }

  private get half(): number {
    return Math.floor(this.material.length / 2)
  }

  /** Regiao de onde ESTE lado tira keystream para enviar. */
  private get sendBase(): number {
    return this.role === 'initiator' ? 0 : this.half
  }

  private get sendLimit(): number {
    return this.role === 'initiator' ? this.half : this.material.length
  }

  private get recvBase(): number {
    return this.role === 'initiator' ? this.half : 0
  }

  private get recvLimit(): number {
    return this.role === 'initiator' ? this.material.length : this.half
  }

  get remainingToSend(): number {
    return this.sendLimit - this.sendBase - this.sendOffset
  }

  /** `true` quando resta menos de 5% da regiao de envio. */
  isLow(): boolean {
    return this.remainingToSend < (this.sendLimit - this.sendBase) * OTP_LOW_WATERMARK
  }

  /**
   * Consome `length` bytes da regiao de envio e os marca como usados.
   * Nao ha caminho de volta: o offset so avanca.
   */
  private takeForSend(length: number): { bytes: Uint8Array; offset: number } {
    if (length > this.remainingToSend) {
      throw new PadExhaustedError(length, this.remainingToSend)
    }
    const start = this.sendBase + this.sendOffset
    const bytes = this.material.slice(start, start + length)
    this.sendOffset += length
    return { bytes, offset: start }
  }

  /** Saida: offset(4) ‖ ciphertext ‖ mac(32) */
  seal(plaintext: Uint8Array, associatedData: Uint8Array = new Uint8Array(0)): Uint8Array {
    const taken = this.takeForSend(plaintext.length + OTP_MAC_BYTES)
    const keystream = taken.bytes.subarray(0, plaintext.length)
    const macKey = taken.bytes.subarray(plaintext.length)

    const ciphertext = new Uint8Array(plaintext.length)
    for (let i = 0; i < plaintext.length; i++) {
      ciphertext[i] = (plaintext[i] ?? 0) ^ (keystream[i] ?? 0)
    }

    const offsetField = new Uint8Array(OTP_OFFSET_BYTES)
    writeUint32BE(taken.offset, offsetField, 0)
    const mac = kdf(
      macKey,
      concat(DOMAIN.otpMac, offsetField, ciphertext, associatedData),
      OTP_MAC_BYTES,
    )
    return concat(offsetField, ciphertext, mac)
  }

  open(sealed: Uint8Array, associatedData: Uint8Array = new Uint8Array(0)): Uint8Array {
    if (sealed.length < OTP_OFFSET_BYTES + OTP_MAC_BYTES) throw new DecryptionFailedError()

    const offset = readUint32BE(sealed, 0)
    const ciphertext = sealed.subarray(OTP_OFFSET_BYTES, sealed.length - OTP_MAC_BYTES)
    const mac = sealed.subarray(sealed.length - OTP_MAC_BYTES)
    const needed = ciphertext.length + OTP_MAC_BYTES

    // O offset tem de cair na regiao do OUTRO lado. Se cair na nossa, ou e
    // bug, ou e alguem tentando nos fazer reusar keystream proprio.
    if (offset < this.recvBase || offset + needed > this.recvLimit)
      throw new DecryptionFailedError()
    if (this.consumedOnReceive.has(offset)) throw new DecryptionFailedError()

    const keystream = this.material.subarray(offset, offset + ciphertext.length)
    const macKey = this.material.subarray(offset + ciphertext.length, offset + needed)

    const expected = kdf(
      macKey,
      concat(DOMAIN.otpMac, sealed.subarray(0, OTP_OFFSET_BYTES), ciphertext, associatedData),
      OTP_MAC_BYTES,
    )
    if (!equalConstantTime(expected, mac)) throw new DecryptionFailedError()

    const plaintext = new Uint8Array(ciphertext.length)
    for (let i = 0; i < ciphertext.length; i++) {
      plaintext[i] = (ciphertext[i] ?? 0) ^ (keystream[i] ?? 0)
    }
    this.consumedOnReceive.add(offset)
    return plaintext
  }

  /** Zera o material na memoria. Chamado pelo modo coacao. */
  wipe(): void {
    sodium().memzero(this.material)
  }
}

export const OTP_KEY_BYTES = SIZES.symmetricKey
